import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Chip,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";

import {
  BucketAsset,
  BucketNumberEnum,
  useListBucketsQuery,
} from "features/trading/buckets";
import { sortMembers } from "./constants";

export const ALL_BUCKETS = "all";

export type BucketScope = BucketNumberEnum | typeof ALL_BUCKETS;

const DEFAULT_BUCKET_NUMBERS = [
  BucketNumberEnum.BUCKET_1,
  BucketNumberEnum.BUCKET_2,
];

interface BucketAssetSelectorProps {
  accountId?: number | null;
  bucketNumbers?: BucketNumberEnum[];
  onAssetIdsChange: (assetIds: number[]) => void;
}

/**
 * Pick a bucket or All - then optionally narrow to specific assets inside it.
 * The asset options are the buckets' own assetsInScope, already loaded alongside the
 * bucket, so this does not search the global asset catalogue
 */
export const BucketAssetSelector = ({
  accountId,
  bucketNumbers = DEFAULT_BUCKET_NUMBERS,
  onAssetIdsChange,
}: BucketAssetSelectorProps) => {
  const [scope, setScope] = useState<BucketScope>(ALL_BUCKETS);
  const [selectedAssetIds, setSelectedAssetIds] = useState<number[]>([]);

  const { data: bucketsData } = useListBucketsQuery(
    { account: accountId as number },
    { skip: !accountId },
  );

  const assetsIn = useCallback(
    (numbers: BucketNumberEnum[]) => {
      const unique: BucketAsset[] = [];
      numbers.forEach((number) => {
        bucketsData?.results
          .find((bucket) => bucket.number === number)
          ?.assets.filter(({ included }) => included)
          .forEach((bucketAsset) => {
            // The same asset can sit in both buckets - take it once.
            if (!unique.some(({ asset }) => asset === bucketAsset.asset)) {
              unique.push(bucketAsset);
            }
          });
      });
      return sortMembers(unique);
    },
    [bucketsData],
  );

  const allAssets = useMemo(
    () => assetsIn(bucketNumbers),
    [assetsIn, bucketNumbers],
  );

  const assetsInScope = useMemo(
    () => (scope === ALL_BUCKETS ? allAssets : assetsIn([scope])),
    [allAssets, assetsIn, scope],
  );

  const selectedAssets = useMemo(
    () => allAssets.filter(({ asset }) => selectedAssetIds.includes(asset)),
    [allAssets, selectedAssetIds],
  );

  const assetIds = useMemo(() => {
    const selectedInScope = assetsInScope.filter(({ asset }) =>
      selectedAssetIds.includes(asset),
    );
    return (selectedInScope.length ? selectedInScope : assetsInScope).map(
      ({ asset }) => asset,
    );
  }, [assetsInScope, selectedAssetIds]);

  useEffect(() => {
    onAssetIdsChange(assetIds);
  }, [onAssetIdsChange, assetIds]);

  const handleScopeChange = (_: unknown, value: BucketScope | null) => {
    if (value !== null) setScope(value);
  };

  const handleAssetsChange = (_: unknown, value: BucketAsset[]) => {
    setSelectedAssetIds(value.map(({ asset }) => asset));
  };

  return (
    <Box display="flex" gap={2} alignItems="flex-start" flexWrap="wrap">
      <ToggleButtonGroup
        exclusive
        size="small"
        color="primary"
        value={scope}
        onChange={handleScopeChange}
      >
        <ToggleButton
          value={ALL_BUCKETS}
          sx={{ paddingX: 2, gap: 1, textTransform: "none" }}
        >
          All
          <Chip
            size="small"
            label={allAssets.length}
            color={scope === ALL_BUCKETS ? "primary" : "default"}
          />
        </ToggleButton>
        {bucketNumbers.map((number) => (
          <ToggleButton
            key={number}
            value={number}
            sx={{ paddingX: 2, gap: 1, textTransform: "none" }}
          >
            Bucket {number}
            <Chip
              size="small"
              label={assetsIn([number]).length}
              color={number === scope ? "primary" : "default"}
            />
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <Autocomplete<BucketAsset, true>
        multiple
        size="small"
        options={assetsInScope}
        value={selectedAssets}
        onChange={handleAssetsChange}
        getOptionLabel={({ symbol, asset__name }) =>
          `${symbol} - ${asset__name}`
        }
        isOptionEqualToValue={(option, value) => option.asset === value.asset}
        sx={{ flexGrow: 1, minWidth: 280 }}
        noOptionsText="No assets in scope"
        renderInput={(params) => (
          <TextField
            {...params}
            label="Assets"
            placeholder={selectedAssets.length ? "" : "All assets in scope"}
          />
        )}
      />
    </Box>
  );
};

export default BucketAssetSelector;
